// @ts-nocheck
import urlUtil from 'url'

import deepmerge from 'deepmerge'
import { parse } from 'qs'
import { extract } from 'query-string'
import { inHTMLData } from 'xss-filters'

import {
  ANALYTICS_TAG_SEARCH_SUBMIT,
  ANALYTICS_TAG_SUGGEST_QUERY_CLICK,
  IS_DEVELOPMENT,
} from 'shared/consts'
import {
  ANALYTICS_TAG_RESULTS,
  SEARCH_STATE_PROPS_WHITELIST,
  SEARCHABLE_FILTERS,
} from 'shared/consts/algolia'
import isAlgoliaAbTestEnabled from 'shared/experiments/browser/algoliaSplit/isAlgoliaAbTestEnabled'
import { validateAlgoliaPznQuery } from 'shared/services/api/helpers/idValidator'
import logger from 'shared/services/logger'
import { parseSearchStateToUrl } from 'shared/utils/algolia'
import { getSearchType } from 'shared/utils/algolia/getSearchType'
import QueryParamsMapper from 'shared/utils/algolia/schema/QueryParamsMapper'
import {
  getTrackingDataFromURL,
  isSearchSubmit,
  isSuggestQueryClick,
} from 'shared/utils/algolia/trackingDataHelper'
import validateSchema from 'shared/utils/json-schema-validator-light'
import {
  setValueByPath,
  walkOverPropsRecursively,
} from 'shared/utils/objectUtils'
import { SearchState, SearchStateProp, SearchType } from 'types/search'
import { DeviceClass, Experiment } from 'types/shopConfig'

import { getPzns, getTenantSpecificFilters } from './filters'
import getDevicePlatform from './getDevicePlatform'
import searchStateSchema from './schema/searchStateSchema'

export const getSearchUrl = ({
  searchUrl: url = '',
  searchState,
  indexName,
  userToken,
}: {
  searchUrl: string
  searchState: SearchState
  indexName: string
  userToken: string
}): string => {
  const { pathname } = urlUtil.parse(url)

  return `${pathname}${parseSearchStateToUrl(searchState, {
    indexName,
    userToken,
  })}`
}

const stringToBooleanTranform = (value: unknown): unknown => {
  if (['true', 'false'].includes(<string>value)) {
    return value === 'true'
  }

  return value
}

/**
 * Parse a generic record from the parsed URL to the valid SearchState
 */
export const parseSearchState = (searchState: GenericRecord): SearchState => {
  if (!searchState) {
    return {}
  }

  const queryParamsMapper = new QueryParamsMapper({})
  const paramsNames = queryParamsMapper.getParamsNames()

  return Object.entries(searchState).reduce<SearchState>(
    (acc, [key, value]) => {
      let newSearchState = acc

      const path = paramsNames.includes(key)
        ? queryParamsMapper.getClearPathByValue(key, '')
        : null

      if (path) {
        newSearchState = setValueByPath<SearchState>({
          obj: newSearchState,
          path,
          value: stringToBooleanTranform(value),
        })

        if (path !== key) {
          delete newSearchState[key]
        }
      } else {
        newSearchState[key] = value
      }

      return newSearchState
    },
    deepmerge({}, searchState)
  )
}

export const urlToSearchState = (search: string): SearchState => {
  try {
    return parseSearchState(parse(search))
  } catch (error) {
    return {}
  }
}

export const normalizeSearchQuery = (
  searchQuery: string | string[]
): string => {
  if (Array.isArray(searchQuery)) {
    return searchQuery[0]?.trim()
  }

  return searchQuery.trim()
}

const escapeStringProps = (searchState: SearchState) =>
  walkOverPropsRecursively<SearchState>(
    {
      transform: propValue =>
        typeof propValue === 'string' ? inHTMLData(propValue) : propValue,
    },
    searchState
  )

const filterUnknownProps = (searchState: SearchState): SearchState =>
  Object.fromEntries(
    Object.entries(searchState).filter(([prop]: SearchStateProp[]) =>
      SEARCH_STATE_PROPS_WHITELIST.includes(prop)
    )
  )

export const sanitizeSearchState = (searchState: SearchState) =>
  escapeStringProps(filterUnknownProps(searchState))

export const sanitizeQuery = (query: string): string => inHTMLData(query)

export const validateSearchStateSchema = (
  searchState: SearchState
): boolean => {
  const { valid, error } = validateSchema(
    {
      schema: searchStateSchema,
      banUnknownProperties: true,
    },
    searchState
  )

  if (!valid && IS_DEVELOPMENT) {
    logger.error(`Got invalid search state: ${error}`)
    logger.error(JSON.stringify(searchState, null, '  '))
  }

  return valid
}

export const modifyPznsQuery = (pzns: string[]): string[] => {
  const modifiedPzns = pzns.map(pzn => validateAlgoliaPznQuery(pzn))
  return (
    modifiedPzns.find(item => item.valid === false)
      ? pzns
      : modifiedPzns.map(item => item.id)
  ).join(' ')
}

/**
 * CRO-5399
 *
 * Used in case when the list of serapated PZNs is queried, i.e.: (upmNVM89R 08123458), to make the
 * response accurate and avoid extra products returned, as in the case with neuralsearch index.
 *
 * This is achieved by disabling neural search and analytics for such queries.
 */
export const getSearchStateWithPzns = searchState => {
  const { query } = searchState

  if (!query) {
    return searchState
  }

  const pzns = getPzns(query)

  if (!pzns.length) {
    return searchState
  }

  const modifiedPzns = modifyPznsQuery(pzns)

  return {
    ...searchState,
    ...{
      query: modifiedPzns,
      analytics: false,
      disableNeuralSearch: true,
    },
  }
}

const getDefaultSearchState = ({
  query,
  indexName,
  deviceClass,
  shouldAddSuggestQueryClickInAnalyticsTag,
  shouldAddSearchSubmitInAnalyticsTag,
  searchType,
  ignoreFilters,
  experiments,
  tenant,
}: {
  query: string
  indexName: string
  deviceClass: DeviceClass
  shouldAddSuggestQueryClickInAnalyticsTag: boolean
  shouldAddSearchSubmitInAnalyticsTag: boolean
  searchType: SearchType
  ignoreFilters: boolean
  experiments: Experiment[] | undefined
  tenant?: string
}): SearchState => ({
  clickAnalytics: true,
  analytics: true,
  analyticsTags: [
    ANALYTICS_TAG_RESULTS,
    getDevicePlatform(deviceClass),
    ...(shouldAddSuggestQueryClickInAnalyticsTag
      ? [ANALYTICS_TAG_SUGGEST_QUERY_CLICK]
      : []),
    ...(shouldAddSearchSubmitInAnalyticsTag
      ? [ANALYTICS_TAG_SEARCH_SUBMIT]
      : []),
    searchType,
    'web', //to differentiate web and app users
  ],
  searchType,
  sortBy: indexName,
  filters: ignoreFilters
    ? SEARCHABLE_FILTERS
    : getTenantSpecificFilters(tenant),
  query: query ? sanitizeQuery(query) : undefined,
  enableABTest: isAlgoliaAbTestEnabled(experiments),
  disableNeuralSearch: false,
})

export const getSearchState = ({
  url,
  indexName,
  deviceClass,
  ignoreFilters = false,
  experiments,
  tenant,
}: {
  url: string
  indexName: string
  deviceClass: DeviceClass
  ignoreFilters: boolean
  experiments?: Experiment[] | undefined
  tenant?: string
}): SearchState => {
  const searchStateFromUrl = urlToSearchState(extract(url))
  const { q, query, ...rest } = searchStateFromUrl
  const searchQuery = normalizeSearchQuery(query || q || '')
  const searchState = sanitizeSearchState(rest)
  const searchType = getSearchType(<GenericRecord>rest)
  const { eventName: searchEvent } = getTrackingDataFromURL(url)
  const shouldAddSuggestQueryClickInAnalyticsTag =
    isSuggestQueryClick(searchEvent)
  const shouldAddSearchSubmitInAnalyticsTag = isSearchSubmit(searchEvent)

  const searchStateDefault = getDefaultSearchState({
    indexName,
    ignoreFilters,
    deviceClass,
    searchType,
    shouldAddSuggestQueryClickInAnalyticsTag,
    shouldAddSearchSubmitInAnalyticsTag,
    query: searchQuery,
    experiments,
    tenant,
  })

  return validateSearchStateSchema(searchState)
    ? {
        ...searchStateDefault,
        ...{ ...searchState, ...{ analytics: searchQuery.length > 0 } },
      }
    : searchStateDefault
}
